/**
 * ч.1401 → ч.1405 → ч.1417 (рішення власника): картка учасника ЕДО — юрособа, ФОП або фізособа + код.
 * Одна форма і на створення, і на правку: не майстер і не картка організації, бо облік у картці не
 * ведеться. Тип і код підтвердженої картки не змінюються: їх звірено із сертифікатом ключа.
 */
import { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { edoParticipants } from '@/api/billing';
import { orgs, type EntityType, type Organization } from '@/api/catalogs';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useOnboarding } from '@/hooks/useOnboarding';
import OrganizationCardPage from '@/pages/OrganizationCardPage';
import { signalUserEdit } from '@/store/formDrafts';

const LOCKED = 'Картку підтверджено ключем КЕП: тип і код змінити не можна';

function errText(e: unknown, org?: Organization): string {
  const d = (e as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof d === 'string' ? d : org ? 'Не вдалося зберегти картку' : 'Не вдалося створити картку';
}

interface FormState { entityType: EntityType; name: string; code: string; email: string }

export function EdoParticipantForm({ org, onCancel }: { org?: Organization; onCancel?: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    entityType: org?.entity_type ?? 'FOP', name: org?.name ?? '',
    code: org?.edrpou ?? org?.inn ?? '', email: org?.email ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const restore = useCallback((v: FormState) => setForm(v), []);
  const draftValue = useMemo(() => form, [form]);
  const draft = useFormDraft(draftValue, restore, { subject: 'цієї картки', clearOnAnyMutation: false });
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const pickType = (entityType: EntityType) => { signalUserEdit(); set({ entityType }); };

  const { entityType, name, code, email } = form;
  const save = useMutation({
    mutationFn: () => {
      const p = { entity_type: entityType, name: name.trim(), code: code.trim(), email: email.trim() || null };
      return org ? edoParticipants.update(org.id, p) : edoParticipants.create(p);
    },
    onSuccess: () => {
      setError(null);
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: ['billing-mode'] });
      qc.invalidateQueries({ queryKey: ['billing-summary'] });
      navigate('/organizations');   // список: кнопка «підтвердити» (заява про приєднання ключем КЕП)
    },
    onError: (e) => setError(errText(e, org)),
  });
  const locked = !!org?.kep_confirmed_at;
  const codeLen = entityType === 'LEGAL_ENTITY' ? 8 : 10;
  const codeOk = new RegExp(`^\\d{${codeLen}}$`).test(code.trim());
  const tab = (value: EntityType, label: string) => (
    <button type="button" className={entityType === value ? 'active' : ''} disabled={locked}
      title={locked ? LOCKED : undefined} onClick={() => pickType(value)}>{label}</button>
  );
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{org ? 'Картка учасника ЕДО' : 'Електронні документи без бухгалтерії'}</h2>
      {!org && (
        <p style={{ color: 'var(--muted)' }}>
          Щоб отримувати, зберігати й підписувати електронні документи, створіть картку учасника ЕДО:
          контрагенти знаходитимуть вас за кодом, а документи зберігатимуться у вашому акаунті.
          Карток може бути кілька (фізособа, ФОП, юрособа). Безкоштовно — 20 документів на всі картки разом.
        </p>
      )}
      {error && <div className="mc__panel mc__panel--warn" role="alert">{error}</div>}
      <div className="seg-tabs" style={{ marginBottom: '0.6rem' }}>
        {tab('FOP', 'ФОП')}
        {tab('LEGAL_ENTITY', 'Юрособа')}
        {tab('INDIVIDUAL', 'Фізична особа')}
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>{entityType === 'LEGAL_ENTITY' ? 'Назва організації' : 'Прізвище, імʼя, по батькові'}</label>
          <input value={name} onChange={(e) => set({ name: e.target.value })} aria-label="Назва учасника" />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{entityType === 'LEGAL_ENTITY' ? 'ЄДРПОУ (8 цифр)' : 'РНОКПП (10 цифр)'}</label>
          <input value={code} onChange={(e) => set({ code: e.target.value })} inputMode="numeric"
            aria-label="Код учасника" disabled={locked} title={locked ? LOCKED : undefined} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Пошта для документів (необовʼязково)</label>
          <input type="email" value={email} onChange={(e) => set({ email: e.target.value })} aria-label="Пошта учасника" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="primary" disabled={save.isPending || name.trim().length < 3 || !codeOk}
          onClick={() => save.mutate()}>{org ? 'зберегти' : 'створити картку'}</button>
        {onCancel && <button type="button" onClick={onCancel}>скасувати</button>}
      </div>
    </div>
  );
}

export default function EdoCardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }), enabled: !!id });
  const back = () => navigate(-1);
  if (!id) return <EdoParticipantForm onCancel={back} />;
  if (list.isLoading) return <div className="card">Завантаження…</div>;
  const org = list.data?.items.find((o) => o.id === id);
  if (!org) {
    return (
      <div className="card">
        <p>Картку не знайдено.</p>
        <button type="button" onClick={() => navigate('/organizations')}>до списку</button>
      </div>
    );
  }
  return <EdoParticipantForm key={org.id} org={org} onCancel={back} />;
}

/** У режимі «ЕДО» організацій немає: стара адреса (відкрита вкладка) веде на картку учасника. */
export function OrganizationRoute() {
  const { id } = useParams<{ id: string }>();
  const { edoOnly } = useOnboarding();
  return edoOnly ? <Navigate to={`/edo/cards/${id}`} replace /> : <OrganizationCardPage />;
}
